// Geo Launch — types and the pure logic behind the tab: which cells are
// selectable, how a selection becomes ad-set groups for review, and what the
// API is sent. Kept out of the components so it is unit-tested.

export const GEOS = ['UK', 'AU', 'CA'] as const;
export type Geo = (typeof GEOS)[number];

/**
 * LIVE / PAUSED: certainly already in the geo. POSSIBLE: something there looks
 * like it (same image, or a name several US creatives share) — launchable
 * after a look. NONE: nothing like it. LAUNCHING: a batch holds it.
 */
export type CellStatus = 'LIVE' | 'PAUSED' | 'POSSIBLE' | 'NONE' | 'LAUNCHING';
export type AdStatus = 'ACTIVE' | 'PAUSED';
export type AdType = 'article' | 'direct_response';
export type DestinationMode = 'new_adset' | 'existing_adset' | 'new_campaign';

export const AD_TYPE_LABEL: Record<AdType, string> = {
  article: 'Article',
  direct_response: 'Direct response',
};

export interface GeoCampaign {
  id: string;
  name: string;
  effectiveStatus: string;
  dailyBudget: number | null;
}

export interface GeoAdset {
  id: string;
  name: string;
  campaignId: string;
  effectiveStatus: string;
}

export interface GeoInfo {
  geo: Geo;
  accountId: string;
  name: string;
  currency: string;
  minorUnits: number;
  campaigns: GeoCampaign[];
  adsets: GeoAdset[];
}

export interface GeoCopy {
  id: string;
  name: string;
  effectiveStatus: string;
  campaignId: string;
  adsetId: string;
  matchedBy: 'label' | 'name_and_image' | 'name' | 'image';
  strength: 'certain' | 'possible';
}

export interface GeoSuggestion {
  campaignId: string | null;
  /** The ad set to launch into by default — also the settings source if a new ad set is chosen. */
  adsetId: string | null;
  link: string | null;
}

export interface GeoCell {
  status: CellStatus;
  copies: GeoCopy[];
  suggestion: GeoSuggestion | null;
}

export interface Winner {
  adId: string;
  adName: string;
  product: string | null;
  campaignName: string | null;
  /** Null for an ad added by ID that the model has not judged a winner. */
  verdict: string | null;
  spend7d: number | null;
  trueRoas7d: number | null;
  targetTrueRoas: number | null;
  firstWonAt: string | null;
  lastWonAt: string | null;
  mediaType: string | null;
  usStatus: string | null;
  destinationUrl: string | null;
  thumbnailUrl: string | null;
  adType: AdType;
  /** Where the ad lives in the US account now. */
  usCampaignId: string | null;
  usCampaignName: string | null;
  usAdsetId: string | null;
  usAdsetName: string | null;
  /** Every US ad of this creative (this row's first): duplicates are one row. */
  adIds: string[];
  /** Why the ad cannot be copied at all (it runs an existing post), else null. */
  blockedReason: string | null;
  geos: Record<Geo, GeoCell>;
  /** Not a winner: added to the table by its ID. */
  addedById?: boolean;
}

export interface MatrixResponse {
  generatedAt: string;
  sourceAccountId: string;
  winners: Winner[];
  geos: GeoInfo[];
  /** The row each ID added by hand shows as (a winner's, when it is one). */
  added: string[];
  /** IDs that could not be added, and why. */
  addErrors: { id: string; error: string }[];
}

/**
 * Ad IDs typed or pasted into "Add US ad by ID": separated by commas, spaces
 * or new lines. Anything that is not an ID comes back in `bad` so the box can
 * say so instead of quietly dropping it.
 */
export function parseAdIds(text: string): { ids: string[]; bad: string[] } {
  const tokens = Array.from(
    new Set(
      text
        .split(/[\s,]+/)
        .map(t => t.trim())
        .filter(Boolean),
    ),
  );
  return {
    ids: tokens.filter(t => /^\d{6,}$/.test(t)),
    bad: tokens.filter(t => !/^\d{6,}$/.test(t)),
  };
}

export interface TextSlot {
  key: string;
  label: string;
  text: string;
}

export interface SourceAd {
  adId: string;
  name: string;
  campaignName: string | null;
  mediaKind: 'image' | 'video' | 'carousel';
  link: string | null;
  images: number;
  videos: number;
  slots: TextSlot[];
  /** Meta's Flexible format: several images/videos in one ad. */
  flexible: boolean;
  flexibleImages: number;
  flexibleVideos: number;
}

export interface ReviewAd {
  adId: string;
  link: string;
  /** Only the slots the operator changed; everything else ships as the US text. */
  texts: Record<string, string>;
}

/** One ad set's worth of the review: a geo, a destination, and the ads going there. */
export interface ReviewGroup {
  key: string;
  geo: Geo;
  mode: DestinationMode;
  campaignId: string;
  templateAdsetId: string;
  adsetId: string;
  sourceCampaignId: string;
  campaignName: string;
  dailyBudget: string;
  adsetName: string;
  ads: ReviewAd[];
}

export interface GeoBatchRequest {
  geo: Geo;
  adStatus: AdStatus;
  /** The dashboard's "who are you" name — attribution in the launch history, not authorization. */
  requestedBy?: string | null;
  adsetName?: string;
  destination:
    | { mode: 'new_adset'; campaignId: string; templateAdsetId: string }
    | { mode: 'existing_adset'; adsetId: string }
    | {
        mode: 'new_campaign';
        sourceCampaignId: string;
        templateAdsetId: string;
        campaignName: string;
        dailyBudget: number;
      };
  ads: {
    sourceAdId: string;
    link: string;
    texts: Record<string, string>;
    product: string | null;
  }[];
}

export interface PlanSummary {
  geo: Geo;
  accountId: string;
  adStatus: AdStatus;
  mode: DestinationMode;
  campaign: { id: string; name: string; effectiveStatus: string } | null;
  newCampaign: {
    name: string;
    dailyBudgetMinor: number;
    currency: string;
    copiedFrom: string;
    status: 'PAUSED';
  } | null;
  adsetName: string;
  settingsFrom: string | null;
  urlTags: string;
  ads: {
    sourceAdId: string;
    name: string;
    link: string;
    images: number;
    videos: number;
    editedTexts: number;
    possibleCopies: { id: string; name: string; matchedBy: string }[];
    /** Copies already in the geo; launching adds another. */
    existingCopies?: { id: string; name: string; effectiveStatus: string }[];
    format: CopyFormat;
    mediaItems: number | null;
    placement: {
      feed: PlacedMedia;
      story: PlacedMedia;
      sameMedia: boolean;
      /** One item everywhere because the ad has no button (Meta refuses a split for those). */
      noButton?: boolean;
    } | null;
  }[];
}

/**
 * How a US ad is copied into a geo (sbly-dash-collector geoLaunch/flexible.js):
 * as its creative stands, as a Flexible ad, or — Flexible ad, geo without the
 * format — as a feed + stories pick of its media.
 */
export type CopyFormat = 'as_us' | 'flexible' | 'placement';

export interface PlacedMedia {
  kind: 'image' | 'video';
  width: number;
  height: number;
  /** Actually the placement's shape, rather than just the closest available. */
  fits: boolean;
}

type PlannedAd = PlanSummary['ads'][number];

const size = (m: PlacedMedia) => `${m.kind} (${m.width}×${m.height})`;

/**
 * One line saying how a checked ad will run in the geo, or null when it is
 * copied exactly as it runs in the US.
 */
export function formatNote(ad: PlannedAd, geo: Geo): string | null {
  if (ad.format === 'flexible') {
    const n = ad.mediaItems ?? 0;
    return `Flexible, like the US ad: all ${n} image${n === 1 ? '' : 's'}/videos and every text.`;
  }
  if (ad.format !== 'placement' || !ad.placement) return null;
  const { feed, story, sameMedia, noButton } = ad.placement;
  const noFlex = `${geo} has no Flexible format, so`;
  if (noButton) {
    const crop = story.fits ? '' : ' (Meta crops it for stories)';
    return `${noFlex} one ${size(feed)} runs everywhere${crop}, with no button like the US ad — Meta doesn't allow separate feed and stories images on a no-button ad.`;
  }
  if (sameMedia) {
    const why = !feed.fits
      ? ' — the US ad has no 4:5 or 1:1 media, so Meta will crop it for feeds'
      : !story.fits
        ? ' — the US ad has no 9:16 media, so Meta will fit it into stories'
        : '';
    return `${noFlex} one ${size(feed)} runs everywhere${why}.`;
  }
  return (
    `${noFlex} feeds get the ${size(feed)}${feed.fits ? '' : ' (closest to 4:5 / 1:1)'}` +
    ` and stories & reels get the ${size(story)}${story.fits ? '' : ' (closest to 9:16 — no vertical media in the US ad)'}.`
  );
}

export type CheckResult =
  | { index: number; ok: true; plan: PlanSummary }
  | { index: number; ok: false; error: string };

export interface QueuedGeoBatch extends PlanSummary {
  batchId: string;
}

export interface GeoLaunchRow {
  launch_id: string;
  batch_id: string;
  geo: Geo;
  account_id: string;
  source_ad_id: string;
  source_ad_name: string | null;
  product: string | null;
  ad_type: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  campaign_created: boolean | null;
  adset_id: string | null;
  adset_name: string | null;
  meta_ad_id: string | null;
  landing_url: string | null;
  ad_status: string | null;
  status: 'PENDING' | 'RUNNING' | 'DONE' | 'FAILED';
  error_message: string | null;
  requested_by: string | null;
  created_at: string;
}

/** Where a batch is in the launch queue (one batch runs at a time). */
export interface GeoQueueState {
  state: 'running' | 'queued' | 'not_queued';
  /** Batches that run before this one. */
  ahead: number;
}

export interface GeoBatchStatus {
  batchId: string;
  launches: GeoLaunchRow[];
  done: boolean;
  succeeded: number;
  failed: number;
  queue: GeoQueueState | null;
}

const CAMPAIGN_BUDGET_PREFIXES = new Set(['CBO', 'ABO']);
const CAMPAIGN_CHANNELS = new Set(['DR', 'SLR']);

/**
 * The campaign's type as the account names it: the stage (TESTING, SCALING,
 * MOF_SCALING), plus the channel when the name gives one (DR = direct
 * response, SLR = sleepratedlab articles).
 *   "CBO - SCALING - DR - CC - INCREMENTAL - DCOP" → "SCALING - DR"
 *   "CBO - MOF_SCALING - LC - DCOP"                → "MOF_SCALING"
 */
export function campaignType(name: string | null | undefined): string {
  const parts = String(name || '')
    .split(' - ')
    .map(p => p.trim().toUpperCase())
    .filter(p => p && !CAMPAIGN_BUDGET_PREFIXES.has(p));
  if (!parts.length) return 'Other';
  return CAMPAIGN_CHANNELS.has(parts[1])
    ? `${parts[0]} - ${parts[1]}`
    : parts[0];
}

export const cellKey = (adId: string, geo: Geo) => `${adId}|${geo}`;

/**
 * Can this cell be ticked? Any cell of a copyable ad except one mid-launch:
 * an ad already live or paused in the geo can be launched again on purpose.
 */
export const isLaunchable = (winner: Winner, geo: Geo) =>
  !winner.blockedReason &&
  Boolean(winner.geos[geo]) &&
  winner.geos[geo].status !== 'LAUNCHING';

/** Missing from the geo: not in it for certain, and copyable at all. */
export const isOpen = (winner: Winner, geo: Geo) =>
  !winner.blockedReason &&
  (winner.geos[geo]?.status === 'NONE' ||
    winner.geos[geo]?.status === 'POSSIBLE');

export function parseCellKey(key: string): { adId: string; geo: Geo } {
  const [adId, geo] = key.split('|');
  return { adId, geo: geo as Geo };
}

/** `M.D.YY` in the ad accounts' timezone — the same working date the US launcher names batches by. */
export function datePrefix(when = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    year: '2-digit',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(when);
  const at = (type: string) => parts.find(p => p.type === type)?.value || '';
  return `${at('month')}.${at('day')}.${at('year')}`;
}

export const defaultAdsetName = (when = new Date()) =>
  `US Winners - ${datePrefix(when)}`;

let groupSeq = 0;
export const newGroupKey = () =>
  `g${Date.now().toString(36)}${(groupSeq += 1)}`;

/**
 * The review's starting point: every selected cell grouped by geo and by the
 * EXISTING ad set suggested for it — launching into an ad set that is already
 * running is the norm; a new ad set (or campaign) is the exception, one
 * dropdown away. Winners suggested for the same ad set share a card.
 */
export function initialGroups(
  cells: string[],
  winners: Map<string, Winner>,
  when = new Date(),
): ReviewGroup[] {
  const groups = new Map<string, ReviewGroup>();
  for (const key of cells) {
    const { adId, geo } = parseCellKey(key);
    const winner = winners.get(adId);
    if (!winner) continue;
    const suggestion = winner.geos[geo]?.suggestion;
    const campaignId = suggestion?.campaignId || '';
    const adsetId = suggestion?.adsetId || '';
    const groupId = `${geo}|${campaignId}|${adsetId}`;
    if (!groups.has(groupId)) {
      groups.set(groupId, {
        key: newGroupKey(),
        geo,
        mode: 'existing_adset',
        campaignId,
        templateAdsetId: adsetId,
        adsetId,
        sourceCampaignId: '',
        campaignName: '',
        dailyBudget: '',
        adsetName: defaultAdsetName(when),
        ads: [],
      });
    }
    groups
      .get(groupId)!
      .ads.push({ adId, link: suggestion?.link || '', texts: {} });
  }
  return GEOS.flatMap(geo =>
    Array.from(groups.values()).filter(g => g.geo === geo),
  );
}

/** Move one ad to another group of the same geo, or to a fresh ad set of its own. */
export function moveAd(
  groups: ReviewGroup[],
  fromKey: string,
  adId: string,
  toKey: string | 'new',
  when = new Date(),
): ReviewGroup[] {
  const from = groups.find(g => g.key === fromKey);
  const ad = from?.ads.find(a => a.adId === adId);
  if (!from || !ad || toKey === fromKey) return groups;
  let next = groups.map(g =>
    g.key === fromKey ? { ...g, ads: g.ads.filter(a => a.adId !== adId) } : g,
  );
  if (toKey === 'new') {
    const index = next.findIndex(g => g.key === fromKey);
    // Its own NEW ad set, settings copied from the one it left — the ad set
    // the card launched into, when it was launching into an existing one.
    const fresh: ReviewGroup = {
      ...from,
      mode: from.mode === 'existing_adset' ? 'new_adset' : from.mode,
      templateAdsetId:
        from.mode === 'existing_adset'
          ? from.adsetId || from.templateAdsetId
          : from.templateAdsetId || from.adsetId,
      adsetId: '',
      key: newGroupKey(),
      adsetName: defaultAdsetName(when),
      ads: [ad],
    };
    next = [...next.slice(0, index + 1), fresh, ...next.slice(index + 1)];
  } else {
    next = next.map(g => (g.key === toKey ? { ...g, ads: [...g.ads, ad] } : g));
  }
  return next.filter(g => g.ads.length);
}

/**
 * What is missing before a group can be sent — shown next to the group, so the
 * server's refusal is never the first time the operator hears about it.
 */
export function groupProblems(group: ReviewGroup, info?: GeoInfo): string[] {
  const problems: string[] = [];
  if (group.mode === 'new_adset') {
    if (!group.campaignId) problems.push('Pick a campaign.');
    const campaign = info?.campaigns.find(c => c.id === group.campaignId);
    if (campaign && !campaign.dailyBudget)
      problems.push(
        `"${campaign.name}" sets budgets per ad set, and a new ad set can only go into a campaign with a daily campaign budget (CBO). Pick a CBO campaign, or launch into one of this campaign's existing ad sets.`,
      );
    if (!group.templateAdsetId)
      problems.push('Pick an ad set to copy settings from.');
  } else if (group.mode === 'existing_adset') {
    if (!group.adsetId) problems.push('Pick the ad set.');
  } else {
    if (!group.sourceCampaignId) problems.push('Pick a campaign to copy.');
    if (!group.templateAdsetId)
      problems.push('Pick an ad set to copy settings from.');
    if (!group.campaignName.trim()) problems.push('Name the new campaign.');
    if (!(Number(group.dailyBudget) > 0)) problems.push('Set a daily budget.');
  }
  const noLink = group.ads.filter(a => !a.link.trim()).length;
  if (noLink)
    problems.push(
      `${noLink} ad${noLink === 1 ? '' : 's'} need${noLink === 1 ? 's' : ''} a landing page.`,
    );
  return problems;
}

export function toBatchRequest(
  group: ReviewGroup,
  adStatus: AdStatus,
  winners: Map<string, Winner>,
  requestedBy: string | null = null,
): GeoBatchRequest {
  const destination: GeoBatchRequest['destination'] =
    group.mode === 'existing_adset'
      ? { mode: 'existing_adset', adsetId: group.adsetId }
      : group.mode === 'new_campaign'
        ? {
            mode: 'new_campaign',
            sourceCampaignId: group.sourceCampaignId,
            templateAdsetId: group.templateAdsetId,
            campaignName: group.campaignName.trim(),
            dailyBudget: Number(group.dailyBudget),
          }
        : {
            mode: 'new_adset',
            campaignId: group.campaignId,
            templateAdsetId: group.templateAdsetId,
          };
  return {
    geo: group.geo,
    adStatus,
    requestedBy,
    ...(group.mode === 'existing_adset'
      ? {}
      : { adsetName: group.adsetName.trim() }),
    destination,
    ads: group.ads.map(a => ({
      sourceAdId: a.adId,
      link: a.link.trim(),
      texts: a.texts,
      product: winners.get(a.adId)?.product ?? null,
    })),
  };
}

/** Rows this campaign has, delivering first — for the pickers. */
export function adsetsOf(
  info: GeoInfo | undefined,
  campaignId: string,
): GeoAdset[] {
  return (info?.adsets || []).filter(s => s.campaignId === campaignId);
}
